import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@finitless/shared/hooks";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { useUpdateConfigImage } from "../../../../hooks/useSettings";
import ImageCropModal from "./ImageCropModal";

const RECOMMENDED_SIZE = {
  dashboard_logo: "900 × 200 px",
  dashboard_favicon: "512 × 512 px",
};

const BrandingImageField = ({ config }) => {
  const { t } = useTranslation("whiteLabel");
  const toast = useToast();
  const updateImage = useUpdateConfigImage();
  const inputRef = useRef(null);
  const [cropImage, setCropImage] = useState(null);

  const handleFile = (event) => {
    const file = event.target.files?.[0];
    if (inputRef.current) inputRef.current.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setCropImage(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleCropped = async (image) => {
    setCropImage(null);
    try {
      await updateImage.mutateAsync({ configId: config.id, image });
      handleApiSuccess(t("branding.imageUpdated"), toast);
    } catch (error) {
      handleApiError(error, toast, {
        defaultMessage: t("branding.imageError"),
      });
    }
  };

  const recommended = RECOMMENDED_SIZE[config.key];
  const isFavicon = config.key === "dashboard_favicon";
  const previewBoxClass = isFavicon
    ? "mt-3 flex h-24 w-24 items-center justify-center overflow-hidden rounded-md border border-gray-700 bg-black p-2 disabled:cursor-not-allowed"
    : "mt-3 flex min-h-[4rem] w-fit max-w-full items-center justify-center overflow-hidden rounded-md border border-gray-700 bg-black p-3 disabled:cursor-not-allowed";
  const imgClass = isFavicon
    ? "max-h-full max-w-full object-contain"
    : "max-h-14 w-auto max-w-full object-contain";

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-900/40 p-4">
      <p className="text-sm font-medium text-white">{config.name}</p>
      {config.description && (
        <p className="mt-1 text-sm text-gray-400">{config.description}</p>
      )}
      {recommended && (
        <p className="mt-1 text-xs text-gray-500">
          {t("branding.recommended", { size: recommended })}
        </p>
      )}

      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={updateImage.isPending}
        className={previewBoxClass}
      >
        {config.value ? (
          <img src={config.value} alt={config.name} className={imgClass} />
        ) : (
          <span className="text-xs text-gray-500">
            {t("branding.uploadImage")}
          </span>
        )}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        data-testid="branding-image-input"
        onChange={handleFile}
      />

      <p className="mt-2 text-xs text-gray-500">
        {updateImage.isPending
          ? t("branding.uploading")
          : t("branding.imageHint")}
      </p>

      {cropImage && (
        <ImageCropModal
          image={cropImage}
          aspectRatio={isFavicon ? 1 : 900 / 200}
          onCrop={handleCropped}
          onClose={() => setCropImage(null)}
        />
      )}
    </div>
  );
};

export default BrandingImageField;
